import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEn from '@angular/common/locales/en';
import {
  LangChangeEvent,
  MissingTranslationHandler,
  TranslateCompiler,
  TranslateLoader,
  TranslateParser,
  TranslateService,
  TranslateStore
} from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { itLocale, deLocale, enGbLocale, frLocale } from 'ngx-bootstrap/locale';
import { Languages } from './languages.enum';
import { TranslationItem } from './translation-item.model';
import { filter, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from 'src/environments/environment';
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeEn);

// ngx-bootstrap
defineLocale('it', itLocale);
defineLocale('de', deLocale);
defineLocale('en', enGbLocale);
defineLocale('fr', frLocale);

@Injectable({
  providedIn: 'root'
})
export class CustomTranslationService extends TranslateService {
  get default_language(): TranslationItem {
    return {
      key: Languages.de,
      name: 'Deutsch'
    };
  }

  get currentLanguage$(): Observable<TranslationItem> {
    return this.currentLang$.asObservable();
  }

  isLangReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private localeBsService: BsLocaleService,
    private http: HttpClient,
    store: TranslateStore,
    currentLoader: TranslateLoader,
    compiler: TranslateCompiler,
    parser: TranslateParser,
    missingTranslationHandler: MissingTranslationHandler
  ) {
    super(
      store,
      currentLoader,
      compiler,
      parser,
      missingTranslationHandler,
      true,
      false,
      true,
      Languages.de
    );
    this.onLangChange.pipe(filter(s => !!s)).subscribe(newLanguage => {
      const newLangItem = this.mapLanguage(newLanguage);
      this.currentLang$.next(newLangItem);
    });
  }

  changeLoaderLoadLang(language: Languages) {
    this.currentLoader = new TranslateHttpLoader(
      this.http,
      environment.apiUrl + '/api/public/ui-translations/',
      ''
    );
    this.reloadLang(language).pipe(take(1)).subscribe(() => {
      this.use(language).subscribe(() => {
        this.isLangReady$.next(true);
      });
    });
  }

  init(language: Languages = Languages.de) {
    this.use(language);
    this.setDefaultLang(language);
  }

  mapLangToLanguage(lang: string): Languages {
    switch (lang) {
      case 'de':
        return Languages.de;
      case 'it':
        return Languages.it;
      case 'fr':
        return Languages.fr;

      default:
        return Languages.de;
    }
  }

  private supportedLanguages: TranslationItem[] = [
    { key: Languages.de, name: 'Deutsch' },
    { key: Languages.en, name: 'English' },
    { key: Languages.fr, name: 'Français' },
    { key: Languages.it, name: 'Italiano' }
  ];

  private currentLang$ = new BehaviorSubject(
    this.mapLanguage({
      lang: this.currentLang ?? this.default_language.key,
      translations: {}
    })
  );

  private mapLanguage(newLanguage: LangChangeEvent): TranslationItem {
    return (
      this.supportedLanguages.find(
        language => newLanguage.lang === language.key
      ) ?? this.default_language
    );
  }
}
